<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OperationsAPI from 'dashboard/api/operations';
import Button from 'dashboard/components-next/button/Button.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  initialMode: {
    type: String,
    default: 'reminder',
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const mode = ref(props.initialMode);
const content = ref('');
const files = ref([]);
const loading = ref(false);
const onlyIfNoReply = ref(false);

const defaultDate = () => {
  const date = new Date(Date.now() + 60 * 60 * 1000);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};
const date = ref(defaultDate());
const title = computed(() =>
  t(
    mode.value === 'message'
      ? 'OPERATIONS.COMPOSER.SCHEDULE_TITLE'
      : 'OPERATIONS.COMPOSER.REMINDER_TITLE'
  )
);

const submit = async () => {
  if (
    !date.value ||
    (mode.value === 'message' && !content.value && !files.value.length)
  )
    return;

  loading.value = true;
  try {
    if (mode.value === 'message') {
      const data = new FormData();
      data.append('conversation_id', props.conversationId);
      data.append('scheduled_message[content]', content.value);
      data.append(
        'scheduled_message[scheduled_at]',
        new Date(date.value).toISOString()
      );
      data.append('scheduled_message[only_if_no_reply]', onlyIfNoReply.value);
      files.value.forEach(file => data.append('attachments[]', file));
      await OperationsAPI.scheduleMessage(data);
    } else {
      await OperationsAPI.createFollowUp({
        conversation_id: props.conversationId,
        conversation_follow_up: {
          note: content.value,
          remind_at: new Date(date.value).toISOString(),
        },
      });
    }
    window.dispatchEvent(new CustomEvent('operations-follow-up-changed'));
    useAlert(t('OPERATIONS.COMPOSER.SUCCESS'));
    emit('close');
  } catch {
    useAlert(t('OPERATIONS.COMPOSER.ERROR'));
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 grid p-4 bg-black/50 place-content-center"
    @click.self="emit('close')"
  >
    <form
      class="w-[42rem] max-w-full overflow-hidden border rounded-xl shadow-xl bg-n-background border-n-weak"
      @submit.prevent="submit"
    >
      <div class="flex items-center justify-between p-5 border-b border-n-weak">
        <h2 class="text-lg font-semibold text-n-slate-12">{{ title }}</h2>
        <button type="button" class="text-n-slate-10" @click="emit('close')">
          <span class="i-lucide-x size-5" />
        </button>
      </div>
      <div class="p-5">
        <div class="flex gap-1 p-1 mb-4 rounded-lg bg-n-alpha-2">
          <button
            type="button"
            class="flex-1 px-3 py-2 text-sm rounded-md"
            :class="
              mode === 'reminder'
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-11'
            "
            @click="mode = 'reminder'"
          >
            {{ t('OPERATIONS.COMPOSER.REMINDER') }}
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-2 text-sm rounded-md"
            :class="
              mode === 'message'
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-11'
            "
            @click="mode = 'message'"
          >
            {{ t('OPERATIONS.COMPOSER.MESSAGE') }}
          </button>
        </div>
        <label class="block mb-4 text-sm text-n-slate-11">
          {{ t('OPERATIONS.COMPOSER.DATE') }}
          <input
            v-model="date"
            required
            type="datetime-local"
            class="w-full px-3 py-2 mt-1 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          />
        </label>
        <div class="p-3 border rounded-xl border-n-weak">
          <WootMessageEditor
            v-model="content"
            :conversation-id="conversationId"
            editor-id="follow-up-composer"
            :placeholder="
              t(
                mode === 'message'
                  ? 'OPERATIONS.COMPOSER.MESSAGE_PLACEHOLDER'
                  : 'OPERATIONS.COMPOSER.NOTE_PLACEHOLDER'
              )
            "
            :min-height="5"
          />
          <label
            v-if="mode === 'message'"
            class="inline-flex items-center gap-2 px-2 py-1 mt-2 text-sm rounded-md cursor-pointer text-n-slate-11 hover:bg-n-alpha-2"
          >
            <span class="i-lucide-paperclip size-4" />
            {{ t('OPERATIONS.COMPOSER.ATTACH') }}
            <input
              type="file"
              multiple
              class="hidden"
              @change="files = [...$event.target.files]"
            />
          </label>
          <p v-if="files.length" class="mt-2 text-xs text-n-slate-10">
            {{ t('OPERATIONS.COMPOSER.FILES', { count: files.length }) }}
          </p>
        </div>
        <label
          v-if="mode === 'message'"
          class="flex items-start gap-3 mt-4 text-sm cursor-pointer text-n-slate-11"
        >
          <input v-model="onlyIfNoReply" type="checkbox" class="mt-1" />
          <span>
            <strong class="block font-medium text-n-slate-12">
              {{ t('OPERATIONS.COMPOSER.ONLY_IF_NO_REPLY') }}
            </strong>
            {{ t('OPERATIONS.COMPOSER.ONLY_IF_NO_REPLY_DESCRIPTION') }}
          </span>
        </label>
      </div>
      <div class="flex justify-end gap-2 p-5 border-t border-n-weak">
        <Button
          type="button"
          :label="t('OPERATIONS.COMPOSER.CANCEL')"
          color="slate"
          @click="emit('close')"
        />
        <Button
          type="submit"
          :label="t('OPERATIONS.COMPOSER.SAVE')"
          :is-loading="loading"
        />
      </div>
    </form>
  </div>
</template>
